import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { FileCsv, ArrowCounterClockwise, CaretDown, CaretUp } from "@phosphor-icons/react";
import api, { formatApiError } from "../api";
import { BrutalButton } from "./Brutal";
import { useAuth } from "../AuthContext";

/**
 * Audit panel for CSV imports — collapsible, sits above the per-row list.
 *
 * Props:
 *  - refreshKey: any  — bump from parent to force a re-fetch after a new import
 *  - onAfterUndo:     — parent re-loads the row list after undo
 */
export default function ExpenseImports({ refreshKey, onAfterUndo }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [imports, setImports] = useState([]);
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const isElevated = !!user && ["owner", "admin", "cfo"].includes(user.role);

  const load = async () => {
    try {
      const { data } = await api.get("/expenses/imports", { params: { limit: 20 } });
      setImports(data);
    } catch (e) {
      // soft fail — audit panel is non-critical
    }
  };
  useEffect(() => { load(); }, [refreshKey]);

  const active = imports.filter((r) => !r.undone_at);
  if (active.length === 0) return null;

  const fmtDate = (iso) =>
    new Date(iso).toLocaleString(i18n.language === "ru" ? "ru-RU" : "en-US", {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  const fmt$ = (n) =>
    Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const undo = async (row) => {
    if (!window.confirm(t("expenses.imports_confirm_undo", { count: row.imported_count }))) return;
    setBusyId(row.id);
    try {
      const { data } = await api.post(`/expenses/imports/${row.id}/undo`);
      toast.success(t("expenses.imports_undo_success", { count: data.deleted_count }));
      await load();
      if (typeof onAfterUndo === "function") onAfterUndo();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section
      className="bg-white border border-zinc-200 rounded-xl overflow-hidden"
      data-testid="expense-imports-panel"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-zinc-50 transition-colors"
        data-testid="expense-imports-toggle"
      >
        <div className="flex items-center gap-2 min-w-0">
          <FileCsv size={18} weight="bold" className="text-zinc-500 shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-zinc-900 truncate">
              {t("expenses.imports_title")}
            </div>
            <div className="text-xs text-zinc-500 truncate">
              {open
                ? t("expenses.imports_subtitle")
                : t("expenses.imports_show", { n: active.length })}
            </div>
          </div>
        </div>
        {open
          ? <CaretUp size={14} weight="bold" className="text-zinc-400" />
          : <CaretDown size={14} weight="bold" className="text-zinc-400" />}
      </button>

      {open && (
        <ul className="border-t border-zinc-200 divide-y divide-zinc-100">
          {active.map((row) => {
            const canUndo = isElevated || row.user_id === user?.id;
            return (
              <li
                key={row.id}
                className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3"
                data-testid={`expense-import-${row.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-zinc-900 truncate" title={row.filename}>
                    {row.filename}
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    {fmtDate(row.created_at)} · {t("expenses.imports_by", { name: row.user_name || row.user_email })}
                  </div>
                  {row.categories?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {row.categories.map((c) => (
                        <span
                          key={c}
                          className="text-[10px] font-medium px-1.5 py-0.5 bg-zinc-100 text-zinc-700 rounded-full"
                        >
                          {c}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0 self-end sm:self-auto">
                  <span
                    className="text-sm tabular-nums text-zinc-900 font-semibold"
                    data-testid={`expense-import-amount-${row.id}`}
                  >
                    {t("expenses.imports_count", {
                      count: row.imported_count,
                      total: fmt$(row.total_amount),
                    })}
                  </span>
                  {canUndo && (
                    <BrutalButton
                      size="sm"
                      variant="secondary"
                      onClick={() => undo(row)}
                      disabled={busyId === row.id}
                      data-testid={`expense-import-undo-${row.id}`}
                      title={t("expenses.imports_undo")}
                      className="!border-rose-300 !text-rose-700 hover:!bg-rose-50"
                    >
                      <ArrowCounterClockwise size={12} weight="bold" />
                      <span className="hidden sm:inline ml-1">{t("expenses.imports_undo")}</span>
                    </BrutalButton>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
